// Constantes do app — sem magic strings/numbers espalhadas.
export const WS_NAMESPACES = {
  inbox: '/inbox',
  production: '/production',
  system: '/system',
} as const;

export type WsNamespace = keyof typeof WS_NAMESPACES;

// Janela 24h do WhatsApp (readme §11) — pode reabrir conversa só nesse intervalo.
export const WINDOW_HOURS = 24;
export const WINDOW_MS = WINDOW_HOURS * 60 * 60 * 1000;

// Paginação cursor (readme §12) — page size default no front.
export const DEFAULT_PAGE_SIZE = 50;

// Reconnect backoff (readme §13 resiliência) — exponencial até 30s.
export const WS_RECONNECT_INITIAL_DELAY_MS = 500;
export const WS_RECONNECT_MAX_DELAY_MS = 30_000;

// Roles (readme §10 RBAC) — hierárquicas:
// GESTOR > GERENTE > VENDEDOR > ATENDENTE > PRODUCAO.
// ATENDENTE (PDV/Guichê, RF-01) e PRODUCAO (RF-04) têm os MENORES ranks de
// propósito: reprovam em todo `RequireRole min=` existente por default
// (fail-closed). O acesso deles às telas dos próprios postos vem de allow-list
// explícita (`oneOf`), nunca da hierarquia.
//
// A escala é PRÓPRIA do front (0..4) e não espelha os números do back
// (`roles.guard.ts` usa 3/5/10/50/100) — só a ORDEM precisa bater.
export const ROLES = ['GESTOR', 'GERENTE', 'VENDEDOR', 'ATENDENTE', 'PRODUCAO'] as const;
export type Role = (typeof ROLES)[number];

/**
 * Rename de 22/09/2026: `SUPERVISOR_VENDAS` virou `GERENTE`. Na janela do deploy a
 * API velha ainda pode responder o nome antigo (e o front velho, o inverso); isto
 * mantém a gerente com o acesso certo enquanto os dois lados não sobem juntos.
 * TODO(remover depois de 2026-10-22).
 */
export function normalizeRole(role: string): Role {
  return role === 'SUPERVISOR_VENDAS' ? 'GERENTE' : (role as Role);
}

export const ROLE_RANK: Record<Role, number> = {
  PRODUCAO: 0,
  ATENDENTE: 1,
  VENDEDOR: 2,
  GERENTE: 3,
  GESTOR: 4,
};

/**
 * Predicados de papel — dono único das perguntas de RBAC que a hierarquia não
 * expressa. Espelham `common/rbac/roles.ts` no back. Use estes em vez de comparar
 * com um papel específico (`role === 'VENDEDOR'`): um papel novo de baixo
 * privilégio cairia no branch errado (foi o que aconteceu com o ATENDENTE).
 */

/** Gerencia a loja (edita configuração, catálogo, equipe). */
export function isManager(role: Role | null | undefined): boolean {
  return role === 'GESTOR' || role === 'GERENTE';
}

/** Opera o guichê presencial (PDV). */
export function isAtendente(role: Role | null | undefined): boolean {
  return role === 'ATENDENTE';
}

/** Opera a tela de Produção (RF-04). */
export function isProducao(role: Role | null | undefined): boolean {
  return role === 'PRODUCAO';
}

/** Papéis com acesso ao módulo presencial (PDV — RF-01). */
export const PDV_ROLES: Role[] = ['GESTOR', 'GERENTE', 'ATENDENTE'];

/**
 * Papéis com acesso à tela de Produção (RF-04). Espelha `PRODUCTION_ROLES` do back
 * (`common/rbac/roles.ts`). Sem o ATENDENTE, como lá.
 */
export const PRODUCTION_ROLES: Role[] = ['GESTOR', 'GERENTE', 'PRODUCAO'];
